/**
 * The interactive generator used both on the landing page and on /generator.
 *
 * The form is **generated from the backend spec** (`GET /playground/spec`), not
 * hardcoded: field order, labels, max lengths, hints and which fields exist per
 * version all come from the same tables the encoder validates against. When the
 * NBU standard changes, this component needs no edit.
 *
 * Preview requests go to the anonymous playground endpoint, so trying the
 * product never costs a logged-in user any quota.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError, request } from '../lib/api'
import { useAuth } from '../lib/auth'
import { dataUrlToBlob, downloadBlob } from '../lib/format'
import type {
  CodeCatalogue,
  ECC,
  GenerateResult,
  QRVersion,
  SpecField,
  VersionSpec,
} from '../lib/types'
import {
  Alert,
  Badge,
  Button,
  Card,
  CopyButton,
  Field,
  Input,
  Select,
  Spinner,
  Textarea,
  cx,
  useToast,
} from './ui'

interface SpecResponse {
  versions: VersionSpec[]
  codes: CodeCatalogue
}

const DEBOUNCE_MS = 400

/** Everything the NBU standard makes mandatory — the preview waits for these. */
const REQUIRED_TO_PREVIEW = ['beneficiary_name', 'iban', 'beneficiary_code', 'purpose'] as const

const DEFAULTS: Record<string, string> = {
  beneficiary_name: '',
  iban: '',
  beneficiary_code: '',
  amount: '',
  purpose: '',
}

export function QRGenerator({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth()
  const { push: toast } = useToast()

  const [spec, setSpec] = useState<SpecResponse | null>(null)
  const [version, setVersion] = useState<QRVersion>('v2')
  const [values, setValues] = useState<Record<string, string>>(DEFAULTS)
  const [ecc, setEcc] = useState<ECC>('M')
  const [scale, setScale] = useState(8)
  const [dark, setDark] = useState('#0F172A')
  const [light, setLight] = useState('#FFFFFF')

  const [result, setResult] = useState<GenerateResult | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [busy, setBusy] = useState(false)
  const [showPayload, setShowPayload] = useState(false)

  // Guards against a slow earlier request overwriting a newer preview.
  const requestSeq = useRef(0)

  useEffect(() => {
    request<SpecResponse>('/playground/spec', { auth: false })
      .then(setSpec)
      .catch(() => toast('Не вдалося завантажити опис стандарту', 'danger'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const currentSpec = useMemo(
    () => spec?.versions.find((v) => v.version === version) ?? null,
    [spec, version],
  )

  const editableFields = useMemo(
    () => currentSpec?.fields.filter((f) => !f.system && f.name !== 'charset') ?? [],
    [currentSpec],
  )

  /** Drop values that the newly selected version does not define. */
  useEffect(() => {
    if (!currentSpec) return
    const allowed = new Set(currentSpec.fields.map((f) => f.name))
    setValues((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => allowed.has(k))))
  }, [currentSpec])

  const setValue = useCallback((name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }))
  }, [])

  const ready = REQUIRED_TO_PREVIEW.every((name) => (values[name] ?? '').trim() !== '')

  // --- live preview, debounced
  useEffect(() => {
    if (!ready) {
      setResult(null)
      setError(null)
      return
    }
    const seq = ++requestSeq.current
    const timer = setTimeout(async () => {
      setBusy(true)
      const payment = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v.trim() !== ''),
      )
      try {
        const res = await request<GenerateResult>('/playground/generate', {
          method: 'POST',
          auth: false,
          body: {
            version,
            charset: '1',
            payment,
            render: { format: 'dataurl', ecc, scale, dark, light },
          },
        })
        if (seq === requestSeq.current) {
          setResult(res)
          setError(null)
        }
      } catch (err) {
        if (seq === requestSeq.current) {
          setResult(null)
          setError(err instanceof ApiError ? err : null)
        }
      } finally {
        if (seq === requestSeq.current) setBusy(false)
      }
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [values, version, ecc, scale, dark, light, ready])

  const fieldError = (name: string) =>
    error?.field === name ? (error.details?.message ?? error.message) : undefined

  const downloadPng = () => {
    if (!result?.image?.dataUrl) return
    downloadBlob(dataUrlToBlob(result.image.dataUrl), 'qr24-nbu.png', 'image/png')
  }

  const downloadSvg = async () => {
    const payment = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== ''))
    try {
      const res = await request<GenerateResult>('/playground/generate', {
        method: 'POST',
        auth: false,
        body: {
          version,
          charset: '1',
          payment,
          render: { format: 'svg', ecc, scale, dark, light },
        },
      })
      if (res.image?.svg) downloadBlob(res.image.svg, 'qr24-nbu.svg', 'image/svg+xml')
    } catch {
      toast('Не вдалося сформувати SVG', 'danger')
    }
  }

  if (!spec || !currentSpec) {
    return (
      <div className="grid place-items-center py-24">
        <Spinner className="h-8 w-8 text-brand-600" />
      </div>
    )
  }

  return (
    <div className={cx('grid gap-6', compact ? 'lg:grid-cols-[1fr_320px]' : 'lg:grid-cols-[1.15fr_1fr]')}>
      {/* ------------------------------------------------------------ form */}
      <Card
        title="Реквізити платежу"
        description={currentSpec.summary}
        actions={
          <Select
            value={version}
            onChange={(e) => setVersion(e.target.value as QRVersion)}
            className="w-auto py-1.5 text-sm"
            aria-label="Версія стандарту"
          >
            {spec.versions.map((v) => (
              <option key={v.version} value={v.version}>
                {v.title}
              </option>
            ))}
          </Select>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {editableFields.map((field) => (
            <SpecInput
              key={field.name}
              field={field}
              value={values[field.name] ?? ''}
              onChange={(v) => setValue(field.name, v)}
              error={fieldError(field.name)}
              codes={spec.codes}
              disabled={Boolean(
                field.exclusiveWith?.some((other) => (values[other] ?? '').trim() !== ''),
              )}
            />
          ))}
        </div>

        {!compact && (
          <details className="mt-6 border-t border-slate-200 pt-4 dark:border-slate-800">
            <summary className="cursor-pointer select-none text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100">
              Вигляд коду
            </summary>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Корекція помилок" hint="H — обовʼязково, якщо в центрі логотип">
                {(id) => (
                  <Select id={id} value={ecc} onChange={(e) => setEcc(e.target.value as ECC)}>
                    <option value="L">L — ~7%</option>
                    <option value="M">M — ~15% (рекомендовано)</option>
                    <option value="Q">Q — ~25%</option>
                    <option value="H">H — ~30%</option>
                  </Select>
                )}
              </Field>
              <Field label={`Масштаб — ${scale}px на модуль`}>
                {(id) => (
                  <input
                    id={id}
                    type="range"
                    min={4}
                    max={12}
                    value={scale}
                    onChange={(e) => setScale(Number(e.target.value))}
                    className="mt-3 w-full accent-brand-600"
                  />
                )}
              </Field>
              <Field label="Колір модулів">
                {(id) => (
                  <div className="flex gap-2">
                    <input
                      id={id}
                      type="color"
                      value={dark}
                      onChange={(e) => setDark(e.target.value.toUpperCase())}
                      className="h-11 w-14 cursor-pointer rounded-xl border border-slate-300 bg-transparent dark:border-slate-700"
                    />
                    <Input value={dark} onChange={(e) => setDark(e.target.value)} />
                  </div>
                )}
              </Field>
              <Field label="Колір фону">
                {(id) => (
                  <div className="flex gap-2">
                    <input
                      id={id}
                      type="color"
                      value={light}
                      onChange={(e) => setLight(e.target.value.toUpperCase())}
                      className="h-11 w-14 cursor-pointer rounded-xl border border-slate-300 bg-transparent dark:border-slate-700"
                    />
                    <Input value={light} onChange={(e) => setLight(e.target.value)} />
                  </div>
                )}
              </Field>
            </div>
          </details>
        )}
      </Card>

      {/* --------------------------------------------------------- preview */}
      <div className="flex flex-col gap-4">
        <Card padded={false} className="overflow-hidden">
          <div className="qr-preview relative grid place-items-center bg-slate-100 p-6 dark:bg-slate-950">
            {busy && (
              <div className="absolute right-3 top-3 text-brand-600">
                <Spinner className="h-4 w-4" />
              </div>
            )}
            {result?.image?.dataUrl ? (
              <img
                src={result.image.dataUrl}
                alt="Згенерований QR-код"
                className="w-full max-w-[280px] rounded-xl"
                width={280}
                height={280}
              />
            ) : (
              <div className="grid aspect-square w-full max-w-[280px] place-items-center rounded-xl border-2 border-dashed border-slate-300 px-6 text-center text-sm text-slate-400 dark:border-slate-700">
                {ready
                  ? 'Виправте помилки у формі — і QR зʼявиться тут'
                  : 'Заповніть отримувача, IBAN, код ЄДРПОУ/РНОКПП та призначення'}
              </div>
            )}
          </div>

          {result && (
            <div className="border-t border-slate-200 px-4 py-3 dark:border-slate-800">
              <div className="mb-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>
                  {result.byteLength} / {result.maxByteLength} байт
                </span>
                <span>
                  {result.image?.modules}×{result.image?.modules} модулів
                </span>
              </div>
              <div
                className="h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
                role="progressbar"
                aria-valuenow={Math.round(result.utilisation * 100)}
              >
                <div
                  className={cx(
                    'h-full rounded-full transition-all',
                    result.utilisation > 0.9 ? 'bg-amber-500' : 'bg-brand-500',
                  )}
                  style={{ width: `${Math.min(100, result.utilisation * 100)}%` }}
                />
              </div>
            </div>
          )}
        </Card>

        {error && !error.field && (
          <Alert tone="danger" title="Не вдалося згенерувати">
            {error.message}
            {error.details?.hint && (
              <p className="mt-1 text-xs opacity-80">{error.details.hint}</p>
            )}
          </Alert>
        )}

        {result?.warnings.map((warning) => (
          <Alert key={warning} tone="warning">
            {warning}
          </Alert>
        ))}

        {result && (
          <>
            <div className="flex flex-wrap gap-2">
              <Button onClick={downloadPng} className="flex-1">
                Завантажити PNG
              </Button>
              <Button variant="secondary" onClick={downloadSvg} className="flex-1">
                SVG
              </Button>
            </div>

            <Card padded={false}>
              <button
                type="button"
                onClick={() => setShowPayload((v) => !v)}
                className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium"
              >
                <span>Вміст QR-коду (payload)</span>
                <svg
                  viewBox="0 0 24 24"
                  className={cx('h-4 w-4 transition', showPayload && 'rotate-180')}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="m6 9 6 6 6-6" strokeLinecap="round" />
                </svg>
              </button>
              {showPayload && (
                <div className="border-t border-slate-200 dark:border-slate-800">
                  <div className="flex justify-end px-2 pt-2">
                    <CopyButton value={result.payload} />
                  </div>
                  <pre className="overflow-x-auto px-4 pb-4 font-mono text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                    {result.payload
                      .split('\n')
                      .map((line, i) => `${String(i + 1).padStart(2, ' ')} │ ${line}`)
                      .join('\n')}
                  </pre>
                </div>
              )}
            </Card>

            {!user && !compact && (
              <Alert tone="brand" title="Потрібні PDF, EPS або пакетна генерація?">
                <p>
                  Зареєструйтеся — це безкоштовно, ви одразу отримаєте API-ключ і 200 запитів
                  на місяць.
                </p>
                <Link to="/register" className="mt-2 inline-block font-semibold text-brand-700 dark:text-brand-300">
                  Створити акаунт →
                </Link>
              </Alert>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// --------------------------------------------------------------- one field

function SpecInput({
  field,
  value,
  onChange,
  error,
  codes,
  disabled,
}: {
  field: SpecField
  value: string
  onChange: (value: string) => void
  error?: string
  codes: CodeCatalogue
  disabled?: boolean
}) {
  const wide = field.maxLength !== null && field.maxLength > 70
  const conflicted = disabled
    ? `Не заповнюйте разом із «${field.exclusiveWith?.join(', ')}»`
    : undefined

  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <Field
        label={field.title.uk}
        required={field.required}
        hint={conflicted ?? field.hint ?? undefined}
        error={error}
        counter={
          field.maxLength && field.kind === 'text' && value
            ? { value: value.length, max: field.maxLength }
            : undefined
        }
      >
        {(id) => {
          const common = { id, disabled, error: Boolean(error) }

          if (field.kind === 'enum' && field.choices) {
            return (
              <Select {...common} value={value} onChange={(e) => onChange(e.target.value)}>
                {field.choices.map((choice) => (
                  <option key={choice} value={choice}>
                    {FUNCTION_LABELS[choice] ?? choice}
                  </option>
                ))}
              </Select>
            )
          }

          if (field.kind === 'category') {
            return (
              <CategoryPicker value={value} onChange={onChange} codes={codes} disabled={disabled} />
            )
          }

          if (field.kind === 'datetime') {
            return (
              <Input
                {...common}
                type="datetime-local"
                step={1}
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )
          }

          if (field.kind === 'lockmask') {
            return (
              <Input
                {...common}
                value={value}
                maxLength={4}
                placeholder={field.example ?? '0000'}
                onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^0-9A-F]/g, ''))}
                className="font-mono uppercase"
              />
            )
          }

          if (field.maxLength && field.maxLength > 100) {
            return (
              <Textarea
                {...common}
                rows={field.maxLength > 200 ? 4 : 2}
                value={value}
                maxLength={field.maxLength}
                placeholder={field.example ?? ''}
                onChange={(e) => onChange(e.target.value)}
              />
            )
          }

          return (
            <Input
              {...common}
              value={value}
              inputMode={field.kind === 'amount' || field.kind === 'taxid' ? 'decimal' : undefined}
              maxLength={field.maxLength ?? undefined}
              placeholder={field.example ?? ''}
              onChange={(e) =>
                onChange(
                  field.kind === 'iban'
                    ? e.target.value.toUpperCase()
                    : field.kind === 'taxid'
                      ? e.target.value.replace(/\D/g, '')
                      : e.target.value,
                )
              }
              className={field.kind === 'iban' ? 'font-mono' : undefined}
            />
          )
        }}
      </Field>

      {field.kind === 'iban' && value.length > 0 && (
        <Badge
          tone={value.replace(/\s/g, '').length === 29 ? 'success' : 'neutral'}
          className="mt-1.5"
        >
          {value.replace(/\s/g, '').length} / 29 символів
        </Badge>
      )}
      {field.kind === 'taxid' && value.length > 0 && (
        <Badge
          tone={value.length === 8 || value.length === 10 ? 'success' : 'neutral'}
          className="mt-1.5"
        >
          {value.length === 8
            ? 'ЄДРПОУ — юридична особа'
            : value.length === 10
              ? 'РНОКПП — фізична особа'
              : `${value.length} цифр — потрібно 8 або 10`}
        </Badge>
      )}
    </div>
  )
}

const FUNCTION_LABELS: Record<string, string> = {
  UCT: 'UCT — звичайний кредитовий переказ',
  ICT: 'ICT — миттєвий переказ',
  XCT: 'XCT — на вибір платника',
}

/** Builds the `CCCC/PPPP` category string from two ISO 20022 dropdowns. */
function CategoryPicker({
  value,
  onChange,
  codes,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  codes: CodeCatalogue
  disabled?: boolean
}) {
  const [category = '', purpose = ''] = value.split('/')

  const emit = (nextCategory: string, nextPurpose: string) => {
    if (!nextCategory && !nextPurpose) return onChange('')
    // A bare purpose is not expressible in CCCC/PPPP — fall back to the category slot.
    if (!nextCategory) return onChange(nextPurpose)
    onChange(nextPurpose ? `${nextCategory}/${nextPurpose}` : nextCategory)
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      <Select
        value={category}
        disabled={disabled}
        aria-label="Категорія (CCCC)"
        onChange={(e) => emit(e.target.value, purpose)}
      >
        <option value="">— категорія —</option>
        {codes.categoryPurpose.map((code) => (
          <option key={code.code} value={code.code}>
            {code.uk} ({code.code})
          </option>
        ))}
      </Select>
      <Select
        value={purpose}
        disabled={disabled || !category}
        aria-label="Призначення (PPPP)"
        onChange={(e) => emit(category, e.target.value)}
      >
        <option value="">— призначення —</option>
        {codes.purpose.map((code) => (
          <option key={code.code} value={code.code}>
            {code.uk} ({code.code})
          </option>
        ))}
      </Select>
    </div>
  )
}
